import React, { useState } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

/* ──────────────────────────────────────────────────────────────────
   DATE HELPERS (self-contained so the popover has no page deps)
   ────────────────────────────────────────────────────────────── */

function toIsoDate(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateLabel(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const DEFAULT_PRESETS = [
  ['7D', 7],
  ['14D', 14],
  ['30D', 30],
  ['90D', 90],
  ['This Month', 'month'],
];

/**
 * Visual date-range picker popover.
 *
 * Two kinds of quick options are supported:
 *  - `presets`: fill the calendar with a relative day window (or the current
 *    month). They stage a from/to selection the user still confirms with
 *    "Apply Range".
 *  - `modePresets`: named ranges that are NOT expressible as calendar days
 *    (e.g. "Billing Cycle", "All Time"). Picking one applies immediately via
 *    `onApply({ mode })` and closes the popover.
 */
export default function DateRangePopover({
  initialRange,
  onApply,
  onClose,
  presets = DEFAULT_PRESETS,
  modePresets = [],
  activeMode = null,
  className,
}) {
  const [viewDate, setViewDate] = useState(() => {
    const d = initialRange?.from ? new Date(initialRange.from) : new Date();
    return Number.isNaN(d.getTime()) ? new Date() : d;
  });

  const [startDate, setStartDate] = useState(() => initialRange?.from || '');
  const [endDate, setEndDate] = useState(() => initialRange?.to || '');
  const [hoverDate, setHoverDate] = useState('');

  const currentYear = viewDate.getFullYear();
  const currentMonth = viewDate.getMonth();

  const monthName = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    setViewDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const nextMonth = () => {
    setViewDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOffset = new Date(currentYear, currentMonth, 1).getDay();

  const handleDayClick = (dayStr) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(dayStr);
      setEndDate('');
    } else if (startDate && !endDate) {
      if (dayStr < startDate) {
        setEndDate(startDate);
        setStartDate(dayStr);
      } else {
        setEndDate(dayStr);
      }
    }
  };

  const effectiveEnd = endDate || (hoverDate && hoverDate > startDate ? hoverDate : '');

  const dayCells = [];
  for (let i = 0; i < firstDayOffset; i++) {
    dayCells.push(<div key={`empty-${i}`} className="h-8 w-8" />);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const mm = String(currentMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const dayStr = `${currentYear}-${mm}-${dd}`;

    const isStart = dayStr === startDate;
    const isEnd = dayStr === endDate;
    const isSelectedBound = isStart || isEnd;
    const isInRange =
      startDate &&
      effectiveEnd &&
      dayStr >= startDate &&
      dayStr <= effectiveEnd;

    dayCells.push(
      <button
        key={dayStr}
        type="button"
        onClick={() => handleDayClick(dayStr)}
        onMouseEnter={() => {
          if (startDate && !endDate) setHoverDate(dayStr);
        }}
        className={cn(
          'relative flex h-8 w-8 items-center justify-center text-xs transition-all',
          isSelectedBound
            ? 'z-10 rounded-full bg-[#007BFF] text-white font-black shadow-sm'
            : isInRange
              ? 'bg-blue-100/90 text-blue-950 font-bold first:rounded-l-full last:rounded-r-full'
              : 'text-slate-700 hover:bg-slate-100 rounded-full font-medium'
        )}
      >
        {day}
      </button>
    );
  }

  const applyPreset = (days) => {
    const now = new Date();
    if (typeof days === 'number') {
      const past = new Date();
      past.setDate(now.getDate() - (days - 1));
      setStartDate(toIsoDate(past));
      setEndDate(toIsoDate(now));
      setViewDate(now);
    } else if (days === 'month') {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(toIsoDate(first));
      setEndDate(toIsoDate(now));
      setViewDate(now);
    }
  };

  const totalDays =
    startDate && endDate
      ? Math.max(
        1,
        Math.round(
          (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)
        ) + 1
      )
      : 0;

  return (
    <div
      className={cn(
        'absolute left-0 top-full mt-2 z-50 w-80 rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl animate-in fade-in zoom-in-95 duration-150',
        className
      )}
    >
      {/* All quick options share one wrapping row and one divider. Splitting
          them into two bordered blocks left a single trailing chip stranded in
          a section of its own. */}
      {(modePresets.length > 0 || presets.length > 0) && (
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 pb-3 mb-3">
          {/* Named ranges a calendar can't express (billing cycle, all time).
              These apply immediately. */}
          {modePresets.map(({ label, value, hint }) => (
            <button
              key={`mode-${value}`}
              type="button"
              title={hint || undefined}
              onClick={() => onApply({ mode: value })}
              className={cn(
                'rounded-lg border px-2 py-1 text-[11px] font-bold transition-all',
                activeMode === value
                  ? 'border-[#007BFF] bg-[#007BFF] text-white shadow-sm'
                  : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-[#007BFF]'
              )}
            >
              {label}
            </button>
          ))}
          {/* Day-window presets, which stage a calendar selection to confirm. */}
          {presets.map(([label, val]) => (
            <button
              key={`preset-${label}`}
              type="button"
              onClick={() => applyPreset(val)}
              className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-[#007BFF] transition-all"
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Month Header & Controls */}
      <div className="flex items-center justify-between mb-3 px-1">
        <strong className="text-sm font-bold text-slate-900">{monthName}</strong>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={prevMonth}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={nextMonth}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Day Name Headers */}
      <div className="grid grid-cols-7 text-center text-[11px] font-bold uppercase text-slate-400 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="h-6 flex items-center justify-center">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-y-1 justify-items-center mb-3">
        {dayCells}
      </div>

      {/* Selected Summary */}
      {startDate && (
        <div className="rounded-xl bg-blue-50 p-2 text-xs text-blue-900 flex items-center justify-between mb-3 border border-blue-100">
          <span className="font-semibold truncate">
            {formatDateLabel(startDate)} {endDate ? `– ${formatDateLabel(endDate)}` : ''}
          </span>
          {totalDays > 0 && (
            <span className="shrink-0 rounded-md bg-blue-200/80 px-2 py-0.5 font-bold text-blue-900 text-[10px]">
              {totalDays} Days
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            if (startDate) {
              onApply({ from: startDate, to: endDate || startDate });
            }
          }}
          disabled={!startDate}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF] px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-slate-300 transition-colors"
        >
          <Check className="h-3.5 w-3.5" />
          Apply Range
        </button>
      </div>
    </div>
  );
}
